import markdownit from "markdown-it";

const md = markdownit({ html: false, linkify: true, typographer: true });

/** HeyBodhi gold leaf mark (matches the in-app AIleafGold asset) for printable documents. */
const HEYBODHI_LEAF_SVG = `<svg viewBox="0 0 47 63" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="HeyBodhi"><path d="M46.7876 7.55524C42.8187 6.45919 41.9933 5.63322 40.8971 1.66475C40.8623 1.53858 40.7478 1.45154 40.6171 1.45154C40.4865 1.45154 40.372 1.53858 40.3371 1.66475C39.2411 5.63361 38.4151 6.459 34.4466 7.55524C34.3205 7.59009 34.2342 7.70459 34.2342 7.83524C34.2342 7.96589 34.3212 8.08038 34.4466 8.11524C38.4155 9.21128 39.2409 10.0373 40.3371 14.0057C40.372 14.1319 40.4865 14.2189 40.6171 14.2189C40.7478 14.2189 40.8623 14.1319 40.8971 14.0057C41.9932 10.0369 42.8191 9.21148 46.7876 8.11524C46.9138 8.08038 47 7.96589 47 7.83524C47 7.70459 46.913 7.59009 46.7876 7.55524Z" fill="#C58C10"/><path d="M34.2447 6.17113C34.2796 6.29731 34.3941 6.38434 34.5247 6.38434C34.6554 6.38434 34.7699 6.29731 34.8047 6.17113C35.3124 4.33323 35.6658 3.98002 37.5037 3.4722C37.6298 3.43735 37.7161 3.32286 37.7161 3.1922C37.7161 3.06155 37.6291 2.94706 37.5037 2.9122C35.6658 2.40458 35.3125 2.05196 34.8047 0.213273C34.7699 0.0870982 34.6554 6.10352e-05 34.5247 6.10352e-05C34.3941 6.10352e-05 34.2796 0.0870982 34.2447 0.213273C33.7371 2.05118 33.3837 2.40439 31.5458 2.9122C31.4196 2.94706 31.3334 3.06155 31.3334 3.1922C31.3334 3.32286 31.4204 3.43735 31.5458 3.4722C33.3837 3.97982 33.7369 4.33245 34.2447 6.17113Z" fill="#C58C10"/><path d="M27.2354 7.83334C27.4032 7.86523 27.3781 7.99331 27.402 8.1063C27.7644 9.82263 27.6571 11.4128 28.3504 13.1375C29.4452 15.8607 33.0624 17.8525 35.3191 19.7621C37.2435 21.3903 39.3411 23.6461 40.6371 25.7341C43.4744 30.3053 45.5902 34.0864 44.9154 39.5802C44.264 44.8847 39.8729 48.5595 33.9532 47.5245C32.4764 47.2664 28.5162 45.6048 27.5817 47.2854C27.4808 47.4669 27.4196 48.18 27.4035 48.4421C27.1972 51.7634 27.2826 57.0559 28.1668 60.2919C28.2777 60.6977 29.237 62.6445 28.3731 62.6666C27.8641 62.6797 27.5299 61.6788 27.4014 61.3094C26.0734 57.4933 26.8184 51.726 26.9848 47.8232C27.0124 47.1757 26.8821 47.0197 26.207 46.825C24.7105 46.3932 22.8319 47.3103 21.3575 47.6025C16.727 48.5201 12.4905 46.4743 10.7743 42.4172C9.04657 38.3326 9.71378 34.0299 11.866 30.215C13.8073 26.7739 15.7774 23.4126 18.6438 20.5387C20.4109 18.7667 22.6822 17.4746 24.3308 15.6067C26.2876 13.3901 27.0118 10.6446 27.2354 7.83334Z" fill="#C58C10" stroke="#C58C10" stroke-width="0.4" stroke-miterlimit="10"/></svg>`;

function escapeHtml(value: string): string {
  return value.replace(/[<>&"]/g, (c) => `&#${c.charCodeAt(0)};`);
}

export interface MarkdownPrintOptions {
  title?: string;
  metaLabel?: string;
  createdAt?: string;
  /** Shown under the HeyBodhi mark in the document header. */
  productLine?: string;
  /** Optional teacher name — rendered as "Prepared by ..." in the meta and footer. */
  authorName?: string;
}

export function renderMarkdownDocumentHtml(markdown: string, options: MarkdownPrintOptions = {}): string {
  const bodyHtml = md.render(markdown);
  const title = escapeHtml(options.title ?? extractTitleFromMarkdown(markdown));
  const meta = options.metaLabel ?? "Document";
  const productLine = escapeHtml(options.productLine ?? "Teacher Scenario Practice");
  const author = options.authorName?.trim() ? escapeHtml(options.authorName.trim()) : "";
  const date = options.createdAt
    ? new Date(options.createdAt).toLocaleDateString()
    : new Date().toLocaleDateString();

  const metaLine = `${meta} · ${date}${author ? ` · Prepared by ${author}` : ""}`;
  const footerNote = `Created with the HeyBodhi Teacher Scenario Practice tool.${author ? ` Prepared by ${author}.` : ""}`;

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${title}</title>
<style>
  :root { color-scheme: light; }
  html, body { margin: 0; background: #faf6ee; }
  body {
    font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    color: #1c2926;
    padding: 32px;
    line-height: 1.55;
  }
  .doc {
    max-width: 760px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.06);
    border-radius: 16px;
    padding: 32px 36px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.06);
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 18px;
    padding-bottom: 14px;
    border-bottom: 1px solid #ece4d2;
  }
  .brand svg { width: 26px; height: 35px; display: block; }
  .brand-text { display: flex; flex-direction: column; line-height: 1.15; }
  .brand-text strong { font-family: 'Literata', Georgia, serif; color: #1f3a30; font-size: 1.05rem; }
  .brand-text small { color: #8a7d63; font-size: 0.7rem; letter-spacing: 0.06em; text-transform: uppercase; }
  h1 { font-family: 'Literata', Georgia, serif; font-size: 1.6rem; margin: 0 0 0.5rem; color: #1f3a30; }
  h2 { font-family: 'Literata', Georgia, serif; font-size: 1.15rem; margin: 1.4rem 0 0.4rem; color: #1f3a30; }
  h3 { font-size: 1rem; margin: 1.1rem 0 0.3rem; color: #1f3a30; }
  p, li { font-size: 0.95rem; }
  ul, ol { padding-left: 1.25rem; }
  blockquote {
    border-left: 3px solid #c58c10;
    background: #faf6ee;
    margin: 1rem 0;
    padding: 0.5rem 0.9rem;
    color: #4b3a14;
    border-radius: 0 8px 8px 0;
  }
  code { background: #f1ece1; padding: 0.1rem 0.35rem; border-radius: 4px; font-size: 0.85em; }
  hr { border: 0; border-top: 1px dashed #c9bfa6; margin: 1.5rem 0; }
  .meta { font-size: 0.78rem; color: #6b6356; margin-bottom: 1rem; }
  .brand-footer {
    margin-top: 28px;
    padding-top: 14px;
    border-top: 1px solid #ece4d2;
    font-size: 0.72rem;
    color: #8a7d63;
  }
  @media print { body { background: #fff; padding: 0; } .doc { box-shadow: none; border: 0; } }
</style>
</head>
<body>
  <main class="doc">
    <header class="brand">
      ${HEYBODHI_LEAF_SVG}
      <span class="brand-text"><strong>HeyBodhi</strong><small>${productLine}</small></span>
    </header>
    <p class="meta">${metaLine}</p>
    ${bodyHtml}
    <footer class="brand-footer">${footerNote}</footer>
  </main>
</body>
</html>`;
}

export function extractTitleFromMarkdown(markdown: string): string {
  const match = markdown.match(/^#\s+(.+?)\s*$/m);
  if (match?.[1]) return match[1].replace(/[*_`]+/g, "").trim();
  return "Document";
}

export function openMarkdownPrintWindow(markdown: string, options: MarkdownPrintOptions = {}): boolean {
  const html = renderMarkdownDocumentHtml(markdown, options);
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  window.setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch {
      /* noop */
    }
  }, 400);
  return true;
}

export async function copyMarkdownAsRichText(
  markdown: string,
  plainFallback?: string,
  options: MarkdownPrintOptions = {},
): Promise<boolean> {
  const plain = plainFallback ?? markdown;
  const html = renderMarkdownDocumentHtml(markdown, { metaLabel: "Scenario Practice Takeaway", ...options });

  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([plain], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      return true;
    } catch {
      /* fall through */
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(plain);
    return true;
  }

  return false;
}
